(ns hai.contacts.editor
  "The assembled contact-editing surfaces: the card holding the composer — as
   a desktop edit modal with a tinted header ([[modal-content]] in [[dialog]])
   or a mobile full-screen editor ([[screen]]). Hosts supply the contact data
   and save wiring as props. Mirrors [[hai.notes.editor]]."
  (:require
   [hai.contacts.form :as form]
   [hai.contacts.header :as header]
   [hai.entity.kebab :as kebab]
   [hai.entity.sections :as sections]
   [hai.mini-apps.frame :as frame]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; The editor's surface — hosts inherit it so the colour lives in one place.
(o/defstyled paper :div
  {:background t/--card-surface})

(o/defstyled edit-modal paper
  {:border-radius "16px"
   :overflow "hidden"
   :display "flex" :flex-direction "column"}
  [:.modal-body
   {:padding "20px 24px 18px"
    :display "flex" :flex-direction "column" :gap "16px"}])

;; Slide-up entrance for the full-screen editor. Garden keyframes are evaluated
;; at macro time, so garden.stylesheet needs no cljs require here.
#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules edit-screen-in
  (garden.stylesheet/at-keyframes
   :contact-edit-screen-in
   [:from {:transform "translateY(100%)"}]
   [:to {:transform "translateY(0)"}]))

(o/defstyled edit-screen paper
  {:position "fixed" :inset "0"
   :z-index "200"
   :display "flex" :flex-direction "column"
   :overscroll-behavior "contain"
   :animation "contact-edit-screen-in 300ms cubic-bezier(.2,.7,.3,1)"}
  ;; The full-screen header sizes its buttons (and the ⋮ beside them) up to
  ;; 32px for touch.
  [header/bar
   {:flex "0 0 auto"
    :padding "14px 16px"}
   [:.bar-action-btn {:width "32px" :height "32px"}]
   [sections/kebab-wrap
    [:button
     {:width "32px" :height "32px" :border-radius "999px"
      :color t/--note-kraft}
     [:&:hover {:background t/--note-kraft-10 :color t/--note-kraft}]
     [:svg {:width "20px" :height "20px"}]]]]
  [:.edit-body
   {:flex "1 1 auto" :min-height "0"
    :padding "16px"
    :overflow-y "auto"
    :overscroll-behavior "contain"
    :display "flex" :flex-direction "column" :gap "14px"}])

(def ^:private visually-hidden
  {:position "absolute" :width "1px" :height "1px" :padding "0" :margin "-1px"
   :overflow "hidden" :clip "rect(0 0 0 0)" :white-space "nowrap" :border "0"})

(defn dialog
  "Radix dialog shell for [[modal-content]]: a transparent panel so the card is
   the only visible surface, with the title kept for a11y. `align=start`
   top-anchors the dialog so the editor growing as you type expands downward
   instead of recentring; the margin-top sits it a comfortable way below the
   viewport top, overriding only the top of Radix's `margin: auto` so it stays
   horizontally centred."
  [{:keys [open? on-close title]} & children]
  [c/DialogRoot
   {:open open?
    :onOpenChange (fn [opening?] (when-not opening? (on-close)))}
   (into [c/DialogContent {:align "start"
                           :style {:background "transparent" :box-shadow "none"
                                   :padding "0" :max-width "760px" :overflow "visible"
                                   :margin-top "10vh"}}
          [c/DialogTitle {:style visually-hidden} title]]
         children)])

(defn- header-actions
  "The ⋮ (Delete, once the contact exists and `on-delete` is given) and the ✕
   (when `on-close` is given)."
  [{:keys [contact on-delete on-close close-size]}]
  [:<>
   (when (and on-delete (:contact/uuid contact))
     [kebab/menu
      {:on-delete      on-delete
       :delete-label   "Delete contact"
       :confirm-prompt "Delete this contact?"
       :aria-label     "Contact actions"}])
   (when on-close
     [frame/action-button {:label "Close"
                           :icon [icons/outlined-close {:size close-size}]
                           :on-click on-close}])])

(defn modal-content
  "The editor surface — in a dialog or straight on a page. `on-save` receives
   the composer's staged payload; the ⋮ shows once the contact exists and
   `on-delete` is given; the ✕ shows when `on-close` is given."
  [{:keys [title contact draft-id auto-focus? show-hint? saving? on-save on-cancel
           on-close on-delete]
    :or {auto-focus? true}}]
  [edit-modal
   [header/bar {:title title}
    [header-actions {:contact contact :on-delete on-delete :on-close on-close :close-size 16}]]
   [:div.modal-body
    [form/contact-form
     {:draft-id    draft-id
      :contact     contact
      :auto-focus? auto-focus?
      :show-hint?  show-hint?
      :saving?     saving?
      :on-save     on-save
      :on-cancel   on-cancel}]]])

(defn screen
  "The full-screen (mobile) editor surface — same contract as
   [[modal-content]]. A fixed overlay, not a dialog: it holds the page body
   still while mounted so nothing behind it scrolls."
  [{:keys [title contact draft-id auto-focus? show-hint? saving? on-save on-cancel
           on-close on-delete]
    :or {auto-focus? true}}]
  (r/with-let [!body-overflow (atom nil)
               ;; A ref callback, not a binding: React detaches the old
               ;; element before it attaches the new, so a keyed remount
               ;; restores and re-locks the body scroll in that order.
               lock-body-scroll! (fn [el]
                                   (let [body-style (.. js/document -body -style)]
                                     (if el
                                       (do (reset! !body-overflow (.-overflow body-style))
                                           (set! (.-overflow body-style) "hidden"))
                                       (set! (.-overflow body-style) @!body-overflow))))]
    [edit-screen {:ref lock-body-scroll!}
     [header/bar {:title title}
      [header-actions {:contact contact :on-delete on-delete :on-close on-close :close-size 20}]]
     [:div.edit-body
      [form/contact-form
       {:draft-id    draft-id
        :contact     contact
        :auto-focus? auto-focus?
        :show-hint?  show-hint?
        :saving?     saving?
        :on-save     on-save
        :on-cancel   on-cancel}]]]))
